import type { ServerSummary } from "@hostdock/types";
import { Badge, formatBytes, StatusBadge, type BadgeTone } from "@hostdock/ui";

const DISPLAY_TONE: Record<string, BadgeTone> = {
  ACTIVE: "success",
  READY: "success",
  NEW: "neutral",
  CONNECTING: "info",
  PROVISIONING: "info",
  TESTING: "info",
  MAINTENANCE: "warning",
  DRAINING: "warning",
  WARNING: "warning",
  OFFLINE: "danger",
  FAILED: "danger",
  RETIRED: "neutral",
};

export function ServerStatusBadge({ status }: { status: string }) {
  if (DISPLAY_TONE[status]) {
    return (
      <Badge tone={DISPLAY_TONE[status]} dot>
        {status.charAt(0) + status.slice(1).toLowerCase()}
      </Badge>
    );
  }
  return <StatusBadge status={status} />;
}

/** Compact usage bar with the value as text (never color alone). */
export function UsageBar({ used, total, label }: { used: number; total: number; label?: string }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const tone = pct >= 90 ? "bg-danger" : pct >= 75 ? "bg-warning" : "bg-accent";
  return (
    <div className="min-w-24" title={label ?? `${formatBytes(used)} of ${formatBytes(total)}`}>
      <div className="flex justify-between text-[12px] tabular-nums">
        <span>{pct.toFixed(0)}%</span>
        {label && <span className="text-subtle">{label}</span>}
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-surface-3">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export const cpuText = (s: ServerSummary) => (s.latest ? `${s.latest.cpuPercent.toFixed(0)}%` : "—");
